import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActiveLandingPage } from '@/hooks/use-active-landing-pages';
import { arrowWidth, NavigationArrows } from '@/components/navigation-arrows';
import { createStyles } from '@/contexts/theme-context';
import {
  FlatList,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Platform,
  useWindowDimensions,
  View,
} from 'react-native';
import {
  LandingPageEventType,
  useReportLandingPageEvent,
} from '@/hooks/use-report-landing-page-event';
import { LandingPageTakeoverContent } from '@/components/landing-pages/landing-page-takeover-content';
import { Modal } from '@/components/modal';
import { NavigationDots } from '@/components/navigation-dots';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export const WEB_TAKEOVER_WIDTH = 560;
export const WEB_TAKEOVER_MAX_HEIGHT = 700;
export const WEB_TAKEOVER_MIN_HEIGHT = 400;

const DOTS_HEIGHT = 20;

interface Props {
  landingPages: ActiveLandingPage[];
  onDismiss: () => void;
  restaurantUUID: string;
  supplierUUID: string;
}

export const LandingPageTakeover: React.FC<Props> = ({
  landingPages,
  onDismiss,
  restaurantUUID,
  supplierUUID,
}) => {
  const { isSmallLayout } = useLayoutContext();
  const { bottom, top } = useSafeAreaInsets();
  const { width: windowWidth } = useWindowDimensions();
  const [styles, theme] = useStyles();

  const listRef = useRef<FlatList<ActiveLandingPage>>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const [pageWidth, setPageWidth] = useState(
    isSmallLayout ? windowWidth : Math.min(WEB_TAKEOVER_WIDTH, windowWidth),
  );

  const onPagerLayout = ({ nativeEvent }: LayoutChangeEvent) => {
    if (!isSmallLayout) {
      return;
    }
    const measured = nativeEvent.layout.width;
    if (measured > 0 && measured !== pageWidth) {
      setPageWidth(measured);
    }
  };

  const onMomentumScrollEnd = ({
    nativeEvent,
  }: NativeSyntheticEvent<NativeScrollEvent>) => {
    setActiveIndex(Math.round(nativeEvent.contentOffset.x / pageWidth));
  };

  const goToPage = (index: number) => {
    const clamped = Math.max(0, Math.min(index, landingPages.length - 1));
    setActiveIndex(clamped);
    listRef.current?.scrollToOffset({
      animated: true,
      offset: clamped * pageWidth,
    });
  };

  const { reportEvent } = useReportLandingPageEvent({
    restaurantUUID,
    supplierUUID,
  });

  // Calyx would discard repeated event reports anyway, but better to keep this and not send dupes in the first place
  const reported = useRef(new Set<string>());

  const report = useCallback(
    (landingPageUUID: string, type: LandingPageEventType) => {
      const key = `${landingPageUUID}:${type}`;
      if (reported.current.has(key)) {
        return;
      }
      reported.current.add(key);
      reportEvent({ landingPageUUID, type }).catch(() => {
        reported.current.delete(key);
      });
    },
    [reportEvent],
  );

  const activePage = landingPages[activeIndex] ?? landingPages[0];

  // Every page the user lands on counts as viewed
  useEffect(() => {
    if (activePage) {
      report(activePage.uuid, 'VIEW');
    }
  }, [activePage, report]);

  // Only the _current_ landing page on screen is counted as dismissed
  const dismiss = () => {
    if (activePage) {
      report(activePage.uuid, 'DISMISS');
    }
    onDismiss();
  };

  const isCarousel = landingPages.length > 1;
  const layout = isSmallLayout ? 'mobile' : 'web';

  // swipe disabled for web, like other places
  // https://github.com/necolas/react-native-web/issues/1021
  const canSwipe = Platform.OS !== 'web';
  const showArrows = isCarousel && (!isSmallLayout || !canSwipe);

  const slide = (page: ActiveLandingPage) => (
    <LandingPageTakeoverContent
      body={page.body}
      callToActionLabel={page.callToActionLabel}
      callToActionUrl={page.callToActionUrl}
      clearArrows={showArrows}
      heroImageUrl={page.heroImageUrl}
      layout={layout}
      onCallToAction={() => report(page.uuid, 'CALL_TO_ACTION')}
      title={page.title}
      uuid={page.uuid}
    />
  );

  const pages = isSmallLayout ? (
    <FlatList<ActiveLandingPage>
      data={landingPages}
      horizontal
      keyExtractor={(page) => page.uuid}
      onMomentumScrollEnd={onMomentumScrollEnd}
      pagingEnabled={canSwipe}
      ref={listRef}
      renderItem={({ item }) => (
        <View style={{ width: pageWidth }}>{slide(item)}</View>
      )}
      scrollEnabled={isCarousel && canSwipe}
      showsHorizontalScrollIndicator={false}
      style={styles.fill}
    />
  ) : (
    <View style={styles.stretch} testID="landing-page-takeover-slide">
      {slide(landingPages[activeIndex] ?? landingPages[0])}
    </View>
  );

  const content = (
    <View
      onLayout={onPagerLayout}
      style={isSmallLayout ? styles.fill : styles.stretch}
    >
      {pages}
      {isCarousel ? (
        <View style={styles.dots}>
          <NavigationDots
            activeIndex={activeIndex}
            count={landingPages.length}
            onChange={goToPage}
          />
        </View>
      ) : null}
      {showArrows ? (
        <NavigationArrows
          activeIndex={activeIndex}
          backgroundColor={
            isSmallLayout
              ? theme.sk.color.bg.primary.default
              : theme.colors.white
          }
          buttonContainerStyle={styles.arrows}
          count={landingPages.length}
          onChange={goToPage}
        />
      ) : null}
    </View>
  );

  return (
    <Modal
      contentContainerStyle={
        isSmallLayout
          ? [
              styles.fill,
              styles.screen,
              { paddingBottom: bottom, paddingTop: top },
            ]
          : styles.stretch
      }
      isVisible
      modalHeaderStyle={isSmallLayout ? styles.screenHeader : styles.cardHeader}
      modalStyle={styles.card}
      onBackButtonPress={dismiss}
      onBackdropPress={isSmallLayout ? undefined : dismiss}
      onModalCloseButtonPress={dismiss}
      showSmallLayoutHeader
      style={isSmallLayout ? styles.screenModal : undefined}
      testID="landing-page-takeover"
    >
      {content}
    </Modal>
  );
};

const useStyles = createStyles((theme) => {
  const dotsRowHeight = DOTS_HEIGHT + theme.sk.spacing.spaceLarge;
  const arrowInset = theme.sk.spacing.spaceSmall;

  return {
    arrows: {
      bottom: dotsRowHeight,
      paddingHorizontal: arrowInset,
      width: arrowWidth + 2 * arrowInset,
    },
    card: {
      maxHeight: WEB_TAKEOVER_MAX_HEIGHT,
      maxWidth: WEB_TAKEOVER_WIDTH,
      minHeight: WEB_TAKEOVER_MIN_HEIGHT,
      width: WEB_TAKEOVER_WIDTH,
    },
    cardHeader: {
      paddingBottom: 0,
    },
    dots: {
      height: dotsRowHeight,
      justifyContent: 'center',
      paddingBottom: theme.sk.spacing.spaceLarge,
    },
    fill: {
      flex: 1,
    },
    screen: {
      backgroundColor: theme.sk.color.bg.primary.default,
    },
    screenHeader: {
      justifyContent: 'flex-end',
    },
    screenModal: {
      margin: 0,
    },
    stretch: {
      flexGrow: 1,
      flexShrink: 1,
      minHeight: 0,
    },
  };
});
